'use client'

import { memo, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Trash2, Plus } from 'lucide-react'
import { resolveBlockType } from '@/lib/notebook/registry/blocks-registry'
import { isStructuralSectionType } from '@/lib/notebook/layout/notebook-layout'
import { useBlockSectionActions } from '@/lib/notebook/core/notebook-store-hooks'
import { NB_BLOCK_SPAWN } from '@/lib/notebook/core/notebook-motion-presets'
import SectionRenderer from './SectionRenderer'
import { DeferredSectionBody } from './DeferredSectionBody'

/** Edge + appends inside the block (not a new section). */
const INLINE_APPEND_TYPES = new Set([
  'block_rule',
  'block_card',
  'block_supporting_notes',
  'block_step_row',
  'block_steps',
  'block_timeline',
])

/** No edge chrome — zone picker, fixed hero */
const NO_EDGE_ACTIONS_TYPES = new Set(['block_zone', 'block_hero'])

function BlockSectionInner({ section, deferBody = false, bodyPriority = false }) {
  const {
    locked,
    spawnId,
    clearSpawnSection,
    removeSection,
    insertSectionAfter,
    appendSectionInline,
    addTab,
    openRichTextInsertMenu,
    openMediaAppend,
    openReferenceAppend,
  } = useBlockSectionActions()

  const blockType = resolveBlockType(section.type)
  const isFixed = isStructuralSectionType(blockType)
  const isTabStrip = blockType === 'block_tabs'
  const showEdgeActions = !NO_EDGE_ACTIONS_TYPES.has(blockType)
  const isSpawn = spawnId === section.id

  useEffect(() => {
    if (!isSpawn) return undefined
    const t = window.setTimeout(() => clearSpawnSection(), 1000)
    return () => window.clearTimeout(t)
  }, [isSpawn, clearSpawnSection])

  const handleAddBlock = () => {
    if (isTabStrip) {
      addTab()
      return
    }
    if (blockType === 'block_richtext') {
      openRichTextInsertMenu(section.id)
      return
    }
    if (blockType === 'block_media') {
      openMediaAppend(section.id)
      return
    }
    if (blockType === 'block_reference') {
      openReferenceAppend(section.id)
      return
    }
    if (INLINE_APPEND_TYPES.has(blockType)) {
      appendSectionInline(section.id)
      return
    }
    void insertSectionAfter(section.id, 'block_richtext')
  }

  return (
    <motion.article
      layout={false}
      id={`sec-${section.id}`}
      data-section-id={section.id}
      data-block-type={blockType}
      className="nb-block-slot group"
      initial={isSpawn ? NB_BLOCK_SPAWN.initial : false}
      animate={isSpawn ? NB_BLOCK_SPAWN.animate : undefined}
      transition={isSpawn ? NB_BLOCK_SPAWN.transition : undefined}
    >
      <div className="nb-block-slot__body">
        {deferBody ? (
          <DeferredSectionBody
            section={section}
            structural={isFixed}
            priority={bodyPriority}
          />
        ) : (
          <SectionRenderer section={section} structural={isFixed} />
        )}
      </div>
      {!locked && showEdgeActions && (
        <div className="nb-block-actions">
          {!isFixed && (
            <button
              type="button"
              className="nb-block-action-btn"
              onClick={() => removeSection(section.id)}
              aria-label="Remove block"
              title="Remove"
            >
              <Trash2 size={13} strokeWidth={2} />
            </button>
          )}
          <button
            type="button"
            className="nb-block-action-btn nb-block-action-btn--add"
            onClick={handleAddBlock}
            aria-label={
              isTabStrip
                ? 'Add tab'
                : blockType === 'block_richtext'
                  ? 'Insert block'
                  : blockType === 'block_media'
                    ? 'Append artifact'
                    : blockType === 'block_reference'
                      ? 'Add link'
                      : 'Add row'
            }
            title={
              isTabStrip
                ? 'Add tab'
                : blockType === 'block_richtext'
                  ? 'Insert block'
                  : blockType === 'block_media'
                    ? 'Append artifact'
                    : blockType === 'block_reference'
                      ? 'Add link'
                      : 'Add row'
            }
          >
            <Plus size={14} strokeWidth={2} />
          </button>
        </div>
      )}
    </motion.article>
  )
}

function sectionPropsEqual(prev, next) {
  const a = prev.section
  const b = next.section
  return (
    a.id === b.id &&
    a.type === b.type &&
    a.data === b.data &&
    a.sort_key === b.sort_key &&
    prev.deferBody === next.deferBody &&
    prev.bodyPriority === next.bodyPriority
  )
}

export default memo(BlockSectionInner, sectionPropsEqual)
